import type { AnimalDto } from '../../interactors/gen/backendClient'

export interface GuestAdoptionEligibility {
  /** The association chose to prepare the certificate and the contract through Petso. */
  shouldPrepareDocuments: boolean
  animal?: Pick<AnimalDto, 'adoption'> | null
  /** Stripe Connect is onboarded and able to take payments. */
  isStripeConnectReady: boolean
  /** Deposit recorded on the adoption, in euros. */
  depositAmount?: number
  /** Whether that deposit has actually been collected from the adopter. */
  depositPaid?: boolean
}

/**
 * What the adopter still owes: the adoption price minus a deposit that has actually been collected.
 * The backend works the balance out the same way when the payment is made, so a deposit recorded
 * later is taken into account there; this only decides whether there is any point in a payment link.
 */
const getRemainingPrice = ({ animal, depositAmount, depositPaid }: GuestAdoptionEligibility): number => {
  const price = animal?.adoption?.priceInEuros

  if (typeof price !== 'number' || Number.isNaN(price)) {
    return 0
  }

  return Math.max(price - (depositPaid ? depositAmount || 0 : 0), 0)
}

/**
 * An adoption without documents can be settled from a payment link, which spares the adopter any
 * account and any signature. That is only possible when the organization can actually be paid
 * online and something is left to collect, so the previous behaviour stays in place otherwise: an
 * organization that never onboarded Stripe Connect keeps creating adoptions exactly as before.
 *
 * Kept free of any client import so it stays unit testable.
 */
export const canCreateGuestAdoption = (eligibility: GuestAdoptionEligibility): boolean => {
  if (eligibility.shouldPrepareDocuments || !eligibility.isStripeConnectReady) {
    return false
  }

  return getRemainingPrice(eligibility) > 0
}
